const diagnosticsEnabledKey = 'firestore-diagnostics-enabled';
const diagnosticsForcedKey = 'firestore-diagnostics-force';
const diagnosticsHeartbeatsKey = 'firestore-diagnostics-heartbeats';

// Roughly a week at one persisted sample every 5 minutes (~200 bytes each,
// well inside the ~5MB origin quota).
const maxPersistedHeartbeats = 2016;

export const getFirestoreDiagnosticsEnabled = (): boolean => {
  try {
    return window.localStorage.getItem(diagnosticsEnabledKey) === 'true';
  } catch {
    // Private-mode / disabled storage: diagnostics simply stay off.
    return false;
  }
};

// Manual escape hatch: set `firestore-diagnostics-force` to 'true' by hand to
// run diagnostics in a plain browser tab (reproduction), where the automatic
// Electron condition would otherwise keep them off. Never auto-written.
export const getFirestoreDiagnosticsForced = (): boolean => {
  try {
    return window.localStorage.getItem(diagnosticsForcedKey) === 'true';
  } catch {
    return false;
  }
};

export const setFirestoreDiagnosticsEnabled = (value: boolean) => {
  try {
    window.localStorage.setItem(diagnosticsEnabledKey, value.toString());
  } catch {
    // Nothing to do — the in-memory flag still drives this session.
  }
};

export const getFirestoreDiagnosticsHeartbeats = (): unknown[] => {
  try {
    const raw = window.localStorage.getItem(diagnosticsHeartbeatsKey);
    if (!raw) {
      return [];
    }
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

// Only the low-frequency heartbeat series is persisted — it is what survives an
// app restart, which is the whole point for a multi-day observation. The
// high-frequency event ring stays in memory: appending to a serialized array is
// O(n) per write, which would make the instrument itself degrade over a session.
export const appendFirestoreDiagnosticsHeartbeat = (sample: unknown) => {
  try {
    const existing = getFirestoreDiagnosticsHeartbeats();
    const next = [...existing, sample].slice(-maxPersistedHeartbeats);
    window.localStorage.setItem(diagnosticsHeartbeatsKey, JSON.stringify(next));
  } catch {
    // Quota exceeded or storage unavailable: drop the sample rather than
    // surfacing a QuotaExceededError into unrelated code paths.
  }
};

export const clearFirestoreDiagnostics = () => {
  try {
    window.localStorage.removeItem(diagnosticsHeartbeatsKey);
  } catch {
    // no-op
  }
};
